import { BaseNode, TreeAdapter } from '@abp/ng.components/tree';
import {
  ConfigStateService,
  generatePassword,
  ListService,
  mapEnumToOptions,
  PagedResultDto,
  PermissionService,
} from '@abp/ng.core';
import {
  Confirmation,
  ConfirmationService,
  getPasswordValidators,
  ToasterService,
} from '@abp/ng.theme.shared';
import {
  EXTENSIONS_IDENTIFIER,
  FormProp,
  FormPropData,
  ePropType,
  generateFormFromProps,
} from '@abp/ng.components/extensible';
import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { finalize, pluck, switchMap, take, tap } from 'rxjs/operators';
import snq from 'snq';
import { DEFAULT_USERS_CREATE_FORM_PROPS, DEFAULT_USERS_EDIT_FORM_PROPS, DEFAULT_USERS_ENTITY_ACTIONS, DEFAULT_USERS_TOOLBAR_ALL, UsersComponent, eIdentityComponents } from '@volo/abp.ng.identity';
import { identityTwoFactorBehaviourOptions } from '@volo/abp.ng.identity';
import {
  IdentityRoleService,
  IdentityUserService,
  OrganizationUnitService,
} from '@volo/abp.ng.identity/proxy';
import {
  GetIdentityUsersInput,
  IdentityRoleDto,
  IdentityUserDto,
  OrganizationUnitDto,
  OrganizationUnitWithDetailsDto,
} from '@volo/abp.ng.identity/proxy';
import { UserService } from '@proxy/users/user.service';
import { ObjectExtensionConstants } from '../constants/object-extension-constants';
import { CommonConstants } from '../constants/common-constants';
import { FiltersCardConfig } from '@proxy/filters/models';
import { FilterPageKeys } from '../constants/filter-page-keys';
import { FilterStateService } from '../shared/services/filter-state.service';

enum UserLockDurationType {
  Second = 1,
  Minute = 60,
  Hour = 60 * 60,
  Day = 60 * 60 * 24,
  Month = 60 * 60 * 24 * 30,
  Year = 60 * 60 * 24 * 365,
}

@Component({
  selector: 'app-ldm-users',
  templateUrl: './ldm-users.component.html',
  styleUrls: ['./ldm-users.component.scss'],
  providers: [
    ListService,
    {
      provide: EXTENSIONS_IDENTIFIER,
      useValue: eIdentityComponents.Users,
    },
    {
      provide: UsersComponent,
      useExisting: LdmUsersComponent,
    },
  ],
  styles: [
    `
      .mh-35 {
        max-height: 35px;
      }
    `,
  ],
})
export class LdmUsersComponent implements OnInit, OnDestroy {
  data: PagedResultDto<IdentityUserDto> = { items: [], totalCount: 0 };

  private sortTracker?: Subscription;

  @ViewChild('modalContent')
  modalContent: TemplateRef<any>;

  form: FormGroup;

  setPasswordForm = this.fb.group({
    newPassword: ['', [Validators.required, ...getPasswordValidators(this.injector)]],
  });

  selected: IdentityUserDto;

  selectedUserRoles: IdentityRoleDto[];

  roles: IdentityRoleDto[];

  selectedOrganizationUnits: OrganizationUnitDto[];

  flatOrganizationUnits: OrganizationUnitDto[];

  visiblePermissions: boolean = false;

  providerKey: string;

  isModalVisible: boolean;

  isSetPasswordModalVisible: boolean;

  modalBusy: boolean = false;

  visibleClaims: boolean = false;

  visibleSessions: boolean = false;

  claimSubject = {} as { id: string; type: 'roles' | 'users' };

  filters = {} as GetIdentityUsersInput;

  organization = {
    response: {} as PagedResultDto<OrganizationUnitWithDetailsDto>,
    nodes: [],
    checkedKeys: [],
    expandedKeys: [],
    selectFn: () => false,
  };

  isLockModalVisible: boolean;

  twoFactor = {
    isModalVisible: false,
    checkboxValue: false,
    isOptional: false,
  };

  lockForm = this.fb.group({
    lockoutEnd: [new Date(), [Validators.required]],
  });

  dateTimePickerProps = {
    defaultValue: new Date(),
    displayName: 'AbpIdentity::DisplayName:LockoutEnd',
    validators: () => [Validators.required],
    name: 'lockoutEnd',
    id: 'lockout-end',
    type: ePropType.DateTime,
  } as Partial<FormProp>;

  entityDisplayName: string | undefined;

  lockDurationTypeOptions = mapEnumToOptions(UserLockDurationType);

  userFiltersConfig: FiltersCardConfig;

  trackByFn: TrackByFunction<AbstractControl> = (index, item) => Object.keys(item)[0] || index;

  get roleGroups(): FormGroup[] {
    return snq(() => (this.form.get('roleNames') as FormArray).controls as FormGroup[], []);
  }

  // Managing roles and organization units are child permissions of editing, so they only gate updates.
  get canManageRoles(): boolean {
    return (
      !this.selected?.id || this.permissionService.getGrantedPolicy('AbpIdentity.Users.Update.ManageRoles')
    );
  }

  get canManageOrganizationUnit(): boolean {
    return (
      !this.selected?.id || this.permissionService.getGrantedPolicy('AbpIdentity.Users.Update.ManageOU')
    );
  }

  onVisiblePermissionChange = (value: boolean) => {
    this.visiblePermissions = value;
  };

  buildUserFiltersConfig() {
    this.userFiltersConfig = {
      title: 'Advanced Filters',
      startCollapsed: false,
      filters: [
        {
          key: 'organizationUnitId',
          label: '::OrganizationUnit',
          type: 'organization-unit',
        },
        {
          key: 'roleId',
          label: 'AbpIdentity::Role',
          type: 'select',
          options: [],
        },
        {
          key: 'userName',
          label: 'AbpIdentity::UserName',
          type: 'text',
        },
        {
          key: 'phoneNumber',
          label: 'AbpIdentity::PhoneNumber',
          type: 'text',
        },
        {
          key: 'emailAddress',
          label: 'AbpIdentity::EmailAddress',
          type: 'text',
        },
        {
          key: 'isLockedOut',
          label: 'AbpIdentity::Lock',
          type: 'select',
          options: [
            { key: 'Yes', value: true },
            { key: 'No', value: false },
          ],
        },
        {
          key: 'notActive',
          label: 'AbpIdentity::NotActive',
          type: 'select',
          options: [
            { key: 'Yes', value: true },
            { key: 'No', value: false },
          ],
        },
      ],
    };

    // Assignable roles instead of the role list: /api/identity/roles needs AbpIdentity.Roles, which
    // partner-facing roles do not have, and a 403 here made the whole users page look broken.
    this.service.getAssignableRoles().subscribe({
      next: res => {
        const roleFilter = this.userFiltersConfig.filters.find(f => f.key === 'roleId');
        if (roleFilter) {
          roleFilter.options = this.filterVisibleRoles(res.items).map(role => ({
            key: role.name,
            value: role.id,
          }));
        }
      },
      // The role filter is optional; the rest of the page must still work without it.
      error: () => undefined,
    });
  }

  constructor(
    public readonly list: ListService<GetIdentityUsersInput>,
    public confirmationService: ConfirmationService,
    public service: IdentityUserService,
    public fb: FormBuilder,
    public toasterService: ToasterService,
    public injector: Injector,
    public configState: ConfigStateService,
    public customUserService: UserService,
    private config: ConfigStateService,
    public roleService: IdentityRoleService,
    public organizationUnitService: OrganizationUnitService,
    private filterState: FilterStateService,
    private permissionService: PermissionService
  ) {}

  ngOnInit() {
    const { key } = identityTwoFactorBehaviourOptions[0];
    this.twoFactor.isOptional =
    this.configState.getFeature('Identity.TwoFactor') === key &&
    this.configState.getSetting('Abp.Identity.TwoFactor.Behaviour') === key;

    //ABP 8.0 @volo\abp.ng.identity has CSS issue on name and surname fields, once this is fixed on their side, we will need to remove the css styles from action list
    const nameProp = DEFAULT_USERS_EDIT_FORM_PROPS.find(prop => prop.name === 'name');
    if (nameProp) {
      Object.defineProperty(nameProp, 'group', {
        value: null, 
        writable: true,
        configurable: true,
      });
    }

    const surnameProp = DEFAULT_USERS_EDIT_FORM_PROPS.find(prop => prop.name === 'surname');
    if (surnameProp) {
      Object.defineProperty(surnameProp, 'group', {
        value: null, 
        writable: true,
        configurable: true,
      });
    }

    //Our LDM Application uses custom Users component page and on ABP 8.0, there are new two menus which is not required for us, so for now, we need to remove them.
    const viewDetailsEntityActionIndex = DEFAULT_USERS_ENTITY_ACTIONS.findIndex(x => x.permission == 'AbpIdentity.Users.ViewDetails');
    if (viewDetailsEntityActionIndex != -1) {
      DEFAULT_USERS_ENTITY_ACTIONS.splice(viewDetailsEntityActionIndex, 1);
    }

    const sessionsEntityActionIndex = DEFAULT_USERS_ENTITY_ACTIONS.findIndex(x => x.permission == 'AbpIdentity.Sessions');
    if (sessionsEntityActionIndex != -1) {
      DEFAULT_USERS_ENTITY_ACTIONS.splice(sessionsEntityActionIndex, 1);
    }

    //The Import and Export toolbar dropdowns are not used in LDM, so they are removed from the users toolbar.
    ['AbpIdentity.Users.Import', 'AbpIdentity.Users.Export'].forEach(permission => {
      const toolbarActionIndex = DEFAULT_USERS_TOOLBAR_ALL.findIndex(x => x.permission == permission);
      if (toolbarActionIndex != -1) {
        DEFAULT_USERS_TOOLBAR_ALL.splice(toolbarActionIndex, 1);
      }
    });

    this.buildUserFiltersConfig();

    // Must run before hookToQuery, which fires the first query immediately.
    const saved = this.filterState.restoreInto<GetIdentityUsersInput>(
      FilterPageKeys.Users,
      this.list,
    );
    if (saved) {
      this.filters = saved.filters;
    }

    this.hookToQuery();

    // Known gap: unlike the other list pages, the restored sort arrow is not pushed back onto
    // the table here. This page renders `abp-extensible-table`, whose ngx-datatable lives in a
    // child component's template, so @ViewChild(DatatableComponent) cannot reach it. The sort
    // itself is restored on the ListService, only the header arrow stays unset.
    this.sortTracker = this.filterState.trackSortChanges(FilterPageKeys.Users, this.list, () => ({
      filters: this.filters,
    }));
  }

  ngOnDestroy() {
    this.sortTracker?.unsubscribe();
  }

  onApplyFilters() {
    this.filterState.saveFrom(FilterPageKeys.Users, this.filters, this.list);
    this.list.get();
  }

  clearFilters() {
    this.filters = {} as GetIdentityUsersInput;
    this.filterState.clear(FilterPageKeys.Users);
    this.list.get();
  }

  convertUnitCodeToIndentation(unitCode: string) {
    const oneLevelOfUnitCodeLength = 5;
    const trimmedUnitCode = unitCode.split('.').join('');
    const indent = trimmedUnitCode.length / oneLevelOfUnitCodeLength;

    const rootUnit = this.flatOrganizationUnits.find(o => !o.parentId);
    let rootUnitIndent = 0;
    if (rootUnit) {
      const trimmedRootUnitCode = rootUnit.code.split('.').join('');
      rootUnitIndent = trimmedRootUnitCode.length / oneLevelOfUnitCodeLength;
    }

    return '-'.repeat((indent - rootUnitIndent) * 2);
  }

  private hookToQuery() {
    this.list
      .hookToQuery(query => this.service.getList({ ...query, ...this.filters }))
      .subscribe(res => {
        this.data = res;
      });
  }

  atLeastOneItemSelected() {
    const validator: ValidatorFn = (formArray: FormArray) => {
      const atLeastOneItemIsChecked = formArray.controls.some(c => Object.values(c.value)[0]);
      return atLeastOneItemIsChecked ? null : { required: true };
    };

    return validator;
  }

  // Mirrors the backend role hierarchy: super admin is assignable only by super admin, admin and
  // partner only by admin or super admin. Roles the edited user already has are kept so an edit
  // cannot silently strip them.
  filterAssignableRoles(items: IdentityRoleDto[]): IdentityRoleDto[] {
    return items.filter(role => {
      if (snq(() => this.selectedUserRoles.some(userRole => userRole.id === role.id))) {
        return true;
      }

      if (role.name === CommonConstants.PartnerRoleName) {
        return this.isSuperAdmin || this.isAdmin;
      }

      return this.isRoleVisible(role);
    });
  }

  // The users list filter only hides the two roles above the caller; partner stays selectable so a
  // partner admin can still filter their own users by role.
  filterVisibleRoles(items: IdentityRoleDto[]): IdentityRoleDto[] {
    return items.filter(role => this.isRoleVisible(role));
  }

  private isRoleVisible(role: IdentityRoleDto): boolean {
    if (role.name === CommonConstants.SuperAdminRoleName) {
      return this.isSuperAdmin;
    }

    if (role.name === CommonConstants.AdminRoleName) {
      return this.isSuperAdmin || this.isAdmin;
    }

    return true;
  }

  private get isSuperAdmin(): boolean {
    return this.currentUserRoleNames.includes(CommonConstants.SuperAdminRoleName);
  }

  private get isAdmin(): boolean {
    return this.currentUserRoleNames.includes(CommonConstants.AdminRoleName);
  }

  private get currentUserRoleNames(): string[] {
    return this.config.getDeep(['currentUser', 'roles']) || [];
  }

  buildForm() {
    const data = new FormPropData(this.injector, this.selected);
    this.form = generateFormFromProps(data);

    this.form.addControl('userUnit', this.fb.control(null, Validators.required));

    this.service.getAssignableRoles().subscribe(({ items }) => {
      this.roles = this.filterAssignableRoles(items);

      this.form.addControl(
        'roleNames',
        this.fb.array(
          this.roles.map(role =>
            this.fb.group({
              [role.name]: [
                this.selected.id
                  ? !!snq(() => this.selectedUserRoles.find(userRole => userRole.id === role.id))
                  : role.isDefault,
              ],
            })
          ),
          null
        )
      );
    });

    if (this.selected.id) {
      this.service.getOrganizationUnits(this.selected.id).subscribe(res => {
        if (res.length > 0) {
          this.form.patchValue({ userUnit: res[0].id });
        }
      });
    }

    this.service.getAvailableOrganizationUnits().subscribe(res => {
      this.organization.response = res;
      this.organization.nodes = new TreeAdapter(res.items as BaseNode[]).getTree();
      this.organization.expandedKeys = res.items.map(item => item.id);
      this.organization.checkedKeys = this.selectedOrganizationUnits.map(unit => unit.id);

      let flatList = new TreeAdapter(res.items as BaseNode[]).getList();
      this.flatOrganizationUnits = [];
      flatList.forEach(element => {
        this.flatOrganizationUnits.push(element as OrganizationUnitWithDetailsDto);
      });
    });
  }

  openModal() {
    this.buildForm();
    this.isModalVisible = true;
  }

  onAdd() {
    this.selected = {} as IdentityUserDto;

    this.selectedUserRoles = [];
    this.selectedOrganizationUnits = [];
    this.openModal();
  }

  onEdit(id: string) {
    this.service
      .get(id)
      .pipe(
        tap(selectedUser => (this.selected = selectedUser)),
        switchMap(() => this.service.getRoles(id)),
        tap(res => (this.selectedUserRoles = res.items || [])),
        switchMap(() => this.service.getOrganizationUnits(id)),
        tap(res => (this.selectedOrganizationUnits = res)),
        take(1)
      )
      .subscribe(() => this.openModal());
  }

  save() {
    if (!this.form.valid) return;
    this.modalBusy = true;

    const { roleNames } = this.form.value;
    const mappedRoleNames = snq(
      () =>
        roleNames.filter(role => !!role[Object.keys(role)[0]]).map(role => Object.keys(role)[0]),
      []
    );

    const selectedOrganizationUnitIds = this.form.get('userUnit').value
      ? [].concat(this.form.get('userUnit').value)
      : [];

    if (this.form.get('userUnit').value) {
      if (!this.selected.extraProperties) {
        this.selected.extraProperties = {};
      }

      this.selected.extraProperties[ObjectExtensionConstants.IdentityUserOrganizationUnitId] =
        this.form.get('userUnit').value;
    }

    const { id } = this.selected;

    (id
      ? this.service.update(id, {
          ...this.selected,
          ...this.form.value,
          roleNames: mappedRoleNames,
          organizationUnitIds: selectedOrganizationUnitIds,
          extraProperties: this.selected.extraProperties,
        })
      : this.service.create({
          ...this.form.value,
          roleNames: mappedRoleNames,
          organizationUnitIds: selectedOrganizationUnitIds,
          extraProperties: this.selected.extraProperties,
        })
    )
      .pipe(finalize(() => (this.modalBusy = false)))
      .subscribe(() => {
        this.list.get();
        this.isModalVisible = false;
      });
  }

  delete(id: string, userName: string) {
    this.confirmationService
      .warn('AbpIdentity::UserDeletionConfirmationMessage', 'Are you sure ?', {
        messageLocalizationParams: [userName],
      })
      .subscribe((status: Confirmation.Status) => {
        if (status === Confirmation.Status.confirm) {
          this.service.delete(id).subscribe(() => this.list.get());
        }
      });
  }

  onManageClaims(id: string) {
    this.claimSubject = {
      id,
      type: 'users',
    };

    this.visibleClaims = true;
  }

  openSessionsModal(user: IdentityUserDto) {
    this.visibleSessions = true;
  }

  unlock(id: string) {
    this.service.unlock(id).subscribe(() => {
      this.toasterService.success('AbpIdentity::UserUnlocked');
      this.list.get();
    });
  }

  openPermissionsModal(providerKey: string, userName?: string) {
    this.providerKey = providerKey;
    this.entityDisplayName = userName;
    setTimeout(() => {
      this.visiblePermissions = true;
    }, 0);
  }

  setPassword() {
    if (this.setPasswordForm.invalid) return;

    this.modalBusy = true;
    this.service
      .updatePassword(this.selected.id, this.setPasswordForm.value as any)
      .pipe(finalize(() => (this.modalBusy = false)))
      .subscribe(() => {
        this.isSetPasswordModalVisible = false;
        this.selected = {} as IdentityUserDto;
        this.setPasswordForm.reset();
      });
  }

  generatePassword() {
    this.setPasswordForm.get('newPassword').setValue(generatePassword());
  }

  lock() {
    const { lockoutEnd } = this.lockForm.value;

    this.modalBusy = true;
    this.service
      .lock(this.selected.id, lockoutEnd.toLocaleString())
      .pipe(finalize(() => (this.modalBusy = false)))
      .subscribe(() => {
        this.isLockModalVisible = false;
        this.lockForm.reset({
          lockoutEnd: new Date(),
        });
        this.list.get();
      });
  }

  setTwoFactor() {
    this.modalBusy = true;
    this.service
      .setTwoFactorEnabled(this.selected.id, this.twoFactor.checkboxValue)
      .pipe(finalize(() => (this.modalBusy = false)))
      .subscribe(() => (this.twoFactor.isModalVisible = false));
  }
}
